
import React from 'react';
import { useSeriesInfo, Episode } from '../../hooks/useSeriesInfo';
import logger from '../../lib/logger';
import { AccountContext } from '../../context/account/AccountContext';
import { SelectedChannelContext } from '../../context/channel/SelectedChannelContext';
import { M3uMedia, DEFAULT_MEDIA_DURATION } from '../../lib/m3u';
import { MimeType } from '../../lib/m3u/m3u-model';
import Loading from '../common/Loading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faBookmark } from '@fortawesome/free-solid-svg-icons';
import { playlistShortlistService } from '../../services/playlist/PlaylistShortlistService';
import styles from '../Category/categories.module.css';

interface SeriesEpisodesProps {
    seriesId: string;
    seriesName: string;
    onBack: () => void;
}

const SeriesEpisodes: React.FC<SeriesEpisodesProps> = ({ seriesId, seriesName, onBack }) => {
    const { data: accountData } = React.useContext(AccountContext);
    const { selectChannel } = React.useContext(SelectedChannelContext);

    const { data: seriesInfo, isLoading, error } = useSeriesInfo({
        baseUrl: accountData?.url || '',
        username: accountData?.username || '',
        password: accountData?.password || '',
        seriesId: seriesId
    });

    if (!accountData) {
        return <div className={styles.error}>Ingen kontoinformasjon tilgjengelig</div>;
    }

    if (isLoading) {
        return <Loading />;
    }

    if (error) {
        return <div className={styles.error}>Feil ved lasting av episoder: {error.message}</div>;
    }

    // Check if seriesInfo and seasons exist
    if (!seriesInfo) {
        return <div className={styles.empty}>Ingen data tilgjengelig for denne serien</div>;
    }

    if (!seriesInfo.episodes || typeof seriesInfo.episodes !== 'object') {
        return <div className={styles.empty}>Ingen episoder funnet for denne serien</div>;
    }

    const buildEpisodeUrl = (episodeId: string, extension: string): string => {
        const { url, username, password } = accountData!;
        const baseUrl = url.replace(/\/$/, '');
        return `${baseUrl}/series/${username}/${password}/${episodeId}.${extension}`;
    };

    const getMimeTypeFromExtension = (extension: string): MimeType => {
        const ext = extension.toLowerCase();
        switch (ext) {
            case 'mp4':
            case 'm4v':
                return MimeType.MP4;
            case 'mkv':
                return MimeType.MP4;
            case 'avi':
                return MimeType.AVI;
            case 'm3u8':
            case 'ts':
            case 'm2ts':
                return MimeType.M2TS;
            default:
                return MimeType.MP4;
        }
    };

    const handleEpisodeClick = (episode: Episode, seasonNumber: string) => {
        const episodeUrl = buildEpisodeUrl(episode.id, episode.container_extension);

        const m3uMedia: M3uMedia = {
            name: `${seriesName} - S${seasonNumber}E${episode.episode_num} - ${episode.title}`,
            location: episodeUrl,
            mimeType: getMimeTypeFromExtension(episode.container_extension),
            duration: episode.info?.duration_secs ?? DEFAULT_MEDIA_DURATION,
            attributes: {
                'tvg-id': episode.id,
                'tvg-name': episode.title,
                'tvg-logo': seriesInfo.info?.cover || '',
                'group-title': seriesName
            }
        };

        selectChannel(m3uMedia);
    };

    const addEpisodeToShortlist = (e: React.MouseEvent, episode: Episode, seasonNumber: string) => {
        e.stopPropagation();

        const episodeUrl = buildEpisodeUrl(episode.id, episode.container_extension);

        const m3uMedia: M3uMedia = {
            name: `${seriesName} - S${seasonNumber}E${episode.episode_num} - ${episode.title}`,
            location: episodeUrl,
            mimeType: getMimeTypeFromExtension(episode.container_extension),
            duration: episode.info?.duration_secs ?? DEFAULT_MEDIA_DURATION,
            attributes: {
                'tvg-id': episode.id,
                'tvg-name': episode.title,
                'tvg-logo': seriesInfo.info?.cover || '',
                'group-title': seriesName
            }
        };

        playlistShortlistService.add(m3uMedia);
    };

    // Flatten all episodes from all seasons into a single array
    const allEpisodes = Object.entries(seriesInfo.episodes).flatMap(([seasonNumber, episodes]) => {
        // Check if episodes is an array
        if (!Array.isArray(episodes)) {
            logger.warn(`Season ${seasonNumber} episodes is not an array:`, episodes);
            return [];
        }
        return episodes.map(episode => ({ episode, seasonNumber }));
    });

    // Check if we have any episodes
    if (allEpisodes.length === 0) {
        return <div className={styles.empty}>Ingen episoder funnet for denne serien</div>;
    }

    // Sort episodes by season and episode number
    allEpisodes.sort((a, b) => {
        const seasonDiff = parseInt(a.seasonNumber) - parseInt(b.seasonNumber);
        if (seasonDiff !== 0) return seasonDiff;
        return a.episode.episode_num - b.episode.episode_num;
    });

    return (
        <div className={styles.streamsContainer}>
            <div className={styles.streamsHeader}>
                <h2 className={styles.title}>{seriesName} ({allEpisodes.length} episoder)</h2>
                <button
                    className={styles.closeButton}
                    onClick={onBack}
                    title="Tilbake til serier"
                    aria-label="Tilbake til serier"
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>

            {seriesInfo.info?.plot && (
                <div className={styles.seriesInfo}>
                    <p className={styles.plot}>{seriesInfo.info.plot}</p>
                </div>
            )}

            <ul className={styles.streamsList}>
                {allEpisodes.map(({ episode, seasonNumber }) => (
                    <li
                        key={episode.id}
                        className={styles.streamItem}
                        onClick={() => handleEpisodeClick(episode, seasonNumber)}
                    >
                        <div className={styles.streamActions}>
                            <div className={styles.tooltipContainer} title="Legg til shortlist">
                                <FontAwesomeIcon
                                    icon={faBookmark}
                                    onClick={(e) => addEpisodeToShortlist(e, episode, seasonNumber)}
                                    className={styles.addIcon}
                                />
                            </div>
                        </div>
                        <div className={styles.streamInfo}>
                            <span className={styles.streamName}>
                                S{seasonNumber}E{episode.episode_num}: {episode.title}
                            </span>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default SeriesEpisodes;